import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Mail, Lock, Info, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// mode: 'login' | 'signup' | 'forgot'
const AuthModal = ({ open, onClose, reason = '' }) => {
  const { login, signup } = useCustomerAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (open) {
      setErr('');
      setEmail('');
      setPassword('');
      setMode('login');
      setForgotSent(false);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      if (mode === 'signup') {
        await signup(email.trim(), password);
        onClose();
      } else if (mode === 'login') {
        await login(email.trim(), password);
        onClose();
      } else if (mode === 'forgot') {
        await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
        setForgotSent(true);
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const titleText =
    mode === 'login' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Reset password';

  return (
    <div
      data-testid="auth-modal"
      className="fixed inset-0 z-[65] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white dark:bg-[#141416] rounded-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-2">
            {mode === 'forgot' && (
              <button
                type="button"
                data-testid="auth-modal-back-to-login"
                onClick={() => { setMode('login'); setErr(''); setForgotSent(false); }}
                className="w-8 h-8 rounded-full border border-neutral-200 dark:border-neutral-700 flex items-center justify-center hover:bg-neutral-50 dark:hover:bg-neutral-800"
                aria-label="Back to sign in"
              >
                <ArrowLeft size={14} />
              </button>
            )}
            <h3 className="font-serif text-[22px] text-neutral-900 dark:text-neutral-100">
              {titleText}
            </h3>
          </div>
          <button
            data-testid="auth-modal-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-neutral-200 dark:border-neutral-700 flex items-center justify-center hover:bg-neutral-50 dark:hover:bg-neutral-800"
          >
            <X size={15} />
          </button>
        </div>

        {mode === 'forgot' && forgotSent ? (
          <div className="p-6 space-y-4" data-testid="forgot-password-success">
            <div className="flex items-start gap-3 rounded-md bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900 p-4">
              <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
              <div className="text-[13px] text-emerald-800 dark:text-emerald-200 leading-relaxed">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link.
                Check your inbox (and spam folder). The link expires in 15 minutes.
              </div>
            </div>
            <button
              type="button"
              data-testid="forgot-password-back-to-login"
              onClick={() => { setMode('login'); setForgotSent(false); setErr(''); }}
              className="btn-brand w-full rounded-lg py-3 text-[14.5px] font-semibold"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            {reason && mode === 'login' && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-[12.5px] text-amber-800 dark:text-amber-200">
                <Info size={14} className="mt-0.5 shrink-0" />
                <span>{reason}</span>
              </div>
            )}

            {mode === 'forgot' && (
              <p className="text-[13px] text-neutral-600 dark:text-neutral-400 leading-relaxed">
                Enter the email you used to sign up. We'll send you a link to reset your password.
              </p>
            )}

            <div>
              <label className="block text-[10.5px] tracking-[0.12em] text-neutral-500 uppercase mb-1.5">Email</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  data-testid="auth-modal-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md pl-9 pr-3 py-2.5 text-[14px] outline-none focus:border-neutral-400 dark:focus:border-neutral-600"
                />
              </div>
            </div>

            {mode !== 'forgot' && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[10.5px] tracking-[0.12em] text-neutral-500 uppercase">Password</label>
                  {mode === 'login' && (
                    <button
                      type="button"
                      data-testid="auth-modal-forgot-password-link"
                      onClick={() => { setMode('forgot'); setErr(''); setPassword(''); }}
                      className="text-[11.5px] text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 underline underline-offset-2"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    data-testid="auth-modal-password-input"
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === 'signup' ? 'At least 6 characters' : 'Your password'}
                    className="w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md pl-9 pr-3 py-2.5 text-[14px] outline-none focus:border-neutral-400 dark:focus:border-neutral-600"
                  />
                </div>
              </div>
            )}

            {err && (
              <p className="text-red-600 text-[13px]" data-testid="auth-modal-error">{err}</p>
            )}

            <button
              data-testid="auth-modal-submit"
              type="submit"
              disabled={loading}
              className={`w-full rounded-lg py-3 text-[14.5px] font-semibold transition-colors ${loading ? 'bg-neutral-400 text-white cursor-not-allowed' : 'btn-brand'}`}
            >
              {loading
                ? 'Please wait…'
                : mode === 'login'
                ? 'Sign in'
                : mode === 'signup'
                ? 'Create account'
                : 'Send reset link'}
            </button>

            {mode !== 'forgot' && (
              <p className="text-center text-[13px] text-neutral-500">
                {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                <button
                  type="button"
                  data-testid="auth-modal-toggle-mode"
                  onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setErr(''); }}
                  className="text-neutral-900 dark:text-neutral-100 underline underline-offset-2"
                >
                  {mode === 'login' ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            )}

            <p className="text-center text-[11.5px] text-neutral-400">
              Signing in lets you track your orders. Use the same email you use at checkout.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
